import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import TopHeader from './TopHeader';
import DisabledAccountBanner from '../common/DisabledAccountBanner';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import { crmAPI } from '../../services/api';
import useBackButton from '../../hooks/useBackButton';

interface ModernLayoutProps {
  children: React.ReactNode;
}

const ModernLayout: React.FC<ModernLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  // Close mobile sidebar on back swipe / back button
  useBackButton(
    useCallback(() => {
      setSidebarOpen(false);
    }, []),
    sidebarOpen,
    50
  );

  // Alerts for cancelled collaborations
  const [cancelledProjects, setCancelledProjects] = useState<any[]>([]);
  const [currentProjectToCancel, setCurrentProjectToCancel] = useState<any | null>(null);

  useEffect(() => {
    if (isAuthenticated && user && user.user_type !== 'hirer') {
      const checkProjects = async () => {
        try {
          const res = await crmAPI.checkCancelledProjects();
          const projects = res.data.data.projects || [];
          
          setCancelledProjects(projects);
          if (projects.length > 0) {
            const firstProj = projects[0];
            setCurrentProjectToCancel(firstProj);

            // Mark as dismissed immediately on the backend so it doesn't show again on reload/re-entry
            if (firstProj.alert_type === 'completed') {
              await crmAPI.dismissCompletedCollabAlert(firstProj.project_id);
            } else {
              await crmAPI.dismissCancelledCollabAlert(firstProj.project_id);
            }
          }
        } catch (err) {
          console.error("Failed to check for project alerts", err);
        }
      };
      checkProjects();
    }
  }, [isAuthenticated, user]);

  const handleCancelProject = async (projectId: string) => {
    try {
      await crmAPI.updateProjectStatus(projectId, 'cancelled');
      
      // Remove from list
      const updated = cancelledProjects.filter(p => p.project_id !== projectId);
      setCancelledProjects(updated);
      
      // Show next one if exists
      if (updated.length > 0) {
        setCurrentProjectToCancel(updated[0]);
        const nextProj = updated[0];
        if (nextProj.alert_type === 'completed') {
          await crmAPI.dismissCompletedCollabAlert(nextProj.project_id);
        } else {
          await crmAPI.dismissCancelledCollabAlert(nextProj.project_id);
        }
      } else {
        setCurrentProjectToCancel(null);
      }
    } catch (err) {
      console.error("Failed to cancel project status", err);
    }
  };

  const handleCompleteProject = async (projectId: string) => {
    try {
      await crmAPI.updateProjectStatus(projectId, 'completed');
      
      const updated = cancelledProjects.filter(p => p.project_id !== projectId);
      setCancelledProjects(updated);
      
      if (updated.length > 0) {
        setCurrentProjectToCancel(updated[0]);
        const nextProj = updated[0];
        if (nextProj.alert_type === 'completed') {
          await crmAPI.dismissCompletedCollabAlert(nextProj.project_id);
        } else {
          await crmAPI.dismissCancelledCollabAlert(nextProj.project_id);
        }
      } else {
        setCurrentProjectToCancel(null);
      }
    } catch (err) {
      console.error("Failed to complete project status", err);
    }
  };

  const handleDismissAlert = (projectId: string) => {
    // Remove from list
    const updated = cancelledProjects.filter(p => p.project_id !== projectId);
    setCancelledProjects(updated);
    if (updated.length > 0) {
      setCurrentProjectToCancel(updated[0]);
      const nextProj = updated[0];
      const dismissCall = async () => {
        if (nextProj.alert_type === 'completed') {
          await crmAPI.dismissCompletedCollabAlert(nextProj.project_id);
        } else {
          await crmAPI.dismissCancelledCollabAlert(nextProj.project_id);
        }
      };
      dismissCall();
    } else {
      setCurrentProjectToCancel(null);
    }
  };

  return (
    <div className="min-h-screen bg-primary-50 dark:bg-gray-900 flex flex-col md:flex-row">
      <div className="print:hidden">
        {/* Mobile Top Header */}
        <TopHeader onMenuClick={() => setSidebarOpen(true)} />

        {/* Sidebar (Desktop & Mobile Drawer) */}
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Main Content Area */}
      <main 
        className={`
          flex-1 min-h-screen transition-all duration-300 
          md:ml-64 print:pt-0 print:ml-0 pb-safe
          ${location.pathname.startsWith('/messages') || location.pathname.startsWith('/chat') ? 'h-screen overflow-hidden print:h-auto print:overflow-visible' : ''}
        `}
        style={{ paddingTop: 'calc(4.5rem + max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)))' }}
      >
        {location.pathname.startsWith('/messages') || location.pathname.startsWith('/chat') ? (
          <>
            <DisabledAccountBanner user={user} />
            {children}
          </>
        ) : (
          <div className="w-full mx-auto pt-0 p-4 md:pt-0 md:p-8 print:p-0">
            <DisabledAccountBanner user={user} />
            {children}
          </div>
        )}
      </main>

      {/* Alert modal for cancelled / completed collaborations */}
      {currentProjectToCancel && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 md:p-8 max-w-md w-full border border-gray-100 dark:border-gray-700 shadow-2xl transform scale-100 transition-all">
            <div className="flex flex-col items-center text-center space-y-4">
              {currentProjectToCancel.alert_type === 'completed' ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-teal-50 dark:bg-teal-950/30 flex items-center justify-center text-teal-500">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Collaboration Completed</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Client <strong>{currentProjectToCancel.client_name}</strong> has marked the collaboration request for <strong>"{currentProjectToCancel.project_title}"</strong> as completed.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Would you like to mark this CRM project as completed?
                  </p>
                  <div className="flex flex-col gap-2.5 w-full pt-4">
                    <button
                      onClick={() => handleCompleteProject(currentProjectToCancel.project_id)}
                      className="w-full px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-xl font-medium shadow-md shadow-teal-500/10 transition-all text-sm"
                    >
                      Yes, Complete Project
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-rose-50 dark:bg-rose-950/30 flex items-center justify-center text-rose-500">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Collaboration Cancelled</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Client <strong>{currentProjectToCancel.client_name}</strong> has cancelled the collaboration request for <strong>"{currentProjectToCancel.project_title}"</strong>.
                  </p>
                  {currentProjectToCancel.cancellation_reason && (
                    <div className="w-full p-3.5 bg-rose-50/50 dark:bg-rose-950/20 border border-rose-100/50 dark:border-rose-900/30 rounded-xl text-left">
                      <p className="text-xs font-black uppercase tracking-wider text-rose-600 dark:text-rose-400 mb-0.5">Cancellation Reason</p>
                      <p className="text-xs text-rose-700 dark:text-rose-300 font-medium">
                        {currentProjectToCancel.cancellation_reason}
                        {currentProjectToCancel.cancellation_details && ` - ${currentProjectToCancel.cancellation_details}`}
                      </p>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Would you like to mark this CRM project as cancelled?
                  </p>
                  <div className="flex flex-col gap-2.5 w-full pt-4">
                    <button
                      onClick={() => handleCancelProject(currentProjectToCancel.project_id)}
                      className="w-full px-4 py-2.5 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white rounded-xl font-medium shadow-md shadow-red-500/10 transition-all text-sm"
                    >
                      Yes, Cancel Project
                    </button>
                  </div>
                </>
              )}

              <div className="flex flex-col gap-2.5 w-full pt-0">
                <button
                  onClick={async () => {
                    navigate('/crm', {
                      state: {
                        selectedProjectId: currentProjectToCancel.project_id,
                        activeTab: 'projects'
                      }
                    });
                    
                    // Remove from list
                    const updated = cancelledProjects.filter(p => p.project_id !== currentProjectToCancel.project_id);
                    setCancelledProjects(updated);
                    if (updated.length > 0) {
                      setCurrentProjectToCancel(updated[0]);
                      const nextProj = updated[0];
                      if (nextProj.alert_type === 'completed') {
                        await crmAPI.dismissCompletedCollabAlert(nextProj.project_id);
                      } else {
                        await crmAPI.dismissCancelledCollabAlert(nextProj.project_id);
                      }
                    } else {
                      setCurrentProjectToCancel(null);
                    }
                  }}
                  className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-teal-600 dark:text-teal-400 border border-teal-200 dark:border-teal-800 rounded-xl font-medium transition-all text-sm flex items-center justify-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Details
                </button>

                <button
                  onClick={() => handleDismissAlert(currentProjectToCancel.project_id)}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-xl font-medium transition-all text-xs"
                >
                  Keep Project Active (Dismiss Alert)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernLayout;
